
export type ProductMockKind = 'sign' | 'meet' | 'stat' | 'pro';

export interface ProductFeature {
  /** Material Symbols (Rounded) ligature name, e.g. "description". */
  icon: string;
  title: string;
  desc: string;
}

export interface Product {
  key: string;
  name: string;
  category: string;
  tagline: string;
  blurb: string;
  bullets: string[];
  /** Detailed capabilities shown as a grid on the Applications page. */
  features: ProductFeature[];
  /** Material Symbols (Rounded) ligature name, e.g. "gavel". */
  icon: string;
  url: string;
  mock: ProductMockKind;
  /** Still being built — shown with a "ޑިވެލޮޕްކުރަމުން ދަނީ" badge on the site. */
  inDevelopment?: boolean;
  /** Hidden from every public marketing surface (kept in data for easy re-enable). */
  hidden?: boolean;
}

const allProducts: Product[] = [
  {
    key: 'wapsign',
    name: 'ވެޕްސައިން',
    category: 'ސޮއިކުރުން',
    tagline: 'ލިޔެކިޔުންތަކުގައި ފަސޭހަކަމާއެކު ޑިޖިޓަލްކޮށް ސޮއިކުރުން',
    blurb:
      'ލިޔެކިޔުން ސޮއިކުރަން ފޮނުވައި، ސޮއިކުރުމުން އަނބުރާ ލިބޭނެ — ޕްރިންޓްކޮށް ސްކޭން ކުރާކަށް ނުޖެހޭނެ',
    bullets: [
      'ސޮއި ބޭނުންވާ ތަނުގައި ސޮއިކުރާ ފަރާތުގެ ސިގްނޭޗަރ ބޮކްސް ބަހައްޓަލެވޭނެ',
      'އެއްފަހަރާ ގިނަ ފަރާތްތަކުގެ ސޮއި އަދި ތާރީޚު ބަހައްޓާލެވޭނެ',
      'ސޮއިކުރި ފައިލް ކައުންސިލްގެ ތައްގަނޑާއެކު ފައިނަލްކުރެވޭނެ',
      'ޕޭމަންޓު ވައުޗަރ ތަކާއި ޕާޗޭސް އޯޑަރުތަކަށް އޮޓޯ ސިގްނޭޗަރ ލެވޭނެ',
    ],
    features: [
      {
        icon: 'send',
        title: 'ފިއްތުމަކުން ފޮނުވުން',
        desc: 'PDF އަޕްލޯޑްކޮށް، ޑްރާފްޓް ރައްކާކޮށް، ފިއްތުމަކުން ސޮއިކުރާ ފަރާތްތަކަށް ފޮނުވާލެވޭ.',
      },
      {
        icon: 'edit_square',
        title: 'ސޮއި ފީލްޑް ބެހެއްޓުން',
        desc: 'ސޮއި، ތާރީޚު، ލިޔުން އަދި ތައްގަނޑުގެ ބޮކްސްތައް ކޮންމެ ސަފްޙާއެއްގައި ޑްރެގްކޮށް ބަހައްޓާ.',
      },
      {
        icon: 'group',
        title: 'ގިނަ ފަރާތުން ސޮއި',
        desc: 'އެއް ލިޔުމަކަށް ގިނަ ފަރާތްތަކުން ސޮއިކުރެވޭ، ބޭނުންނަމަ ތަރުތީބުން.',
      },
      {
        icon: 'signature',
        title: 'ރައްކާކުރި ސޮއި',
        desc: 'ސޮއި އެއްފަހަރު ރައްކާކޮށް، ކޮންމެ ފަހަރަކު އަލުން ކުރެހުމެއް ނެތި ބޭނުންކުރެވޭ.',
      },
      {
        icon: 'approval',
        title: 'ކައުންސިލްގެ ތައްގަނޑު',
        desc: 'ފައިނަލް PDF ގައި ކައުންސިލްގެ ރަސްމީ ތައްގަނޑު އޮޓަމެޓިކުން ޖަހައިދޭ.',
      },
      {
        icon: 'task',
        title: 'ސޮއިކުރި PDF',
        desc: 'ހުރިހާ ސޮއެއް ނިމުމުން ސޮއިކުރި PDF އޮޓަމެޓިކުން ތައްޔާރުކޮށް ޑައުންލޯޑް ކުރެވޭ.',
      },
      {
        icon: 'checklist',
        title: 'ސްޓޭޓަސް ބެލުން',
        desc: 'ސޮއިކުރި އަދި ބާކީ ފަރާތްތައް ބަލައި، ސަބަބާއެކު ދޫކޮށްލެވޭ.',
      },
      {
        icon: 'merge',
        title: 'PDF އެއްކުރުން',
        desc: '20 އަކާ ހަމައަށް PDF ނުވަތަ ފޮޓޯ އެއް ފައިލަކަށް އެއްކޮށްލެވޭ.',
      },
    ],
    icon: 'draw',
    url: 'https://sign.wapplify.app',
    mock: 'sign',
  },
  {
    key: 'wapmeet',
    name: 'ވެޕްމީޓް',
    category: 'ބައްދަލުވުންތައް',
    tagline: 'ކައުންސިލް އަދި އ.ތ.މ ކޮމެޓީގެ ބައްދަލުވުންތައް ފެށިގެން ނިމެންދެން',
    blurb:
      'އެޖެންޑާއިން ފެށިގެން ނިންމުމާ ހަމައަށް — ބައްދަލުވުމުގެ ހުރިހާ މަރުހަލާއެއް އެއް ތަނަކުން، ދިވެހިން.',
    bullets: [
      'އެޖެންޑާއަށް މައްސަލަ ހުށަހެޅުމާއި އެޖެންޑާ ތައްޔާރުކޮށް ފޮނުވުން',
      'ޔައުމިއްޔާ، ހާޒިރީ އަދި ނިންމުން ތައްޔާރުކޮށް ފައިނަލްކުރުން',
      'ބައްދަލުވުމުން ނިންމާ ނިންމުންތައް ތަންފީޒުކުރާ މިންވަރު ރިކޯޑުކުރުން',
      'ބައްދަލުވުންތަކުގެ ތަފާސްހިސާބުތައް ބަލާލުން',
    ],
    features: [
      {
        icon: 'playlist_add_check',
        title: 'އެޖެންޑާ ހުށަހެޅުމާއި ފާސްކުރުން',
        desc: 'މެންބަރުން އެޖެންޑާ އައިޓަމް ހުށަހަޅައި، ރައީސް ފާސްކުރޭ ނުވަތަ ބަދަލު ހޯއްދަވާ.',
      },
      {
        icon: 'description',
        title: 'އެޖެންޑާ PDF ތާނައިން',
        desc: 'ލެޓަރހެޑާއެކު ރަސްމީ އެޖެންޑާ ތާނަ PDF އޮޓަމެޓިކުން ތައްޔާރުކޮށްދޭ.',
      },
      {
        icon: 'draw',
        title: 'އެޖެންޑާ ސޮއިކޮށް ފޮނުވުން',
        desc: 'ވެޕްސައިން މެދުވެރިކޮށް އެޖެންޑާ ސޮއިކޮށް، ކަނޑައެޅި ފަރާތްތަކަށް އީމެއިލްކުރެވޭ.',
      },
      {
        icon: 'edit_note',
        title: 'ޔައުމިއްޔާ ލިޔުން',
        desc: 'ހާޒިރީ، ބަހުސް، ހުށަހެޅުންތައް އަދި ބައްދަލުވުމުގެ ތަފްސީލް އެއް ތަނަކުން ލިޔެވޭ.',
      },
      {
        icon: 'how_to_vote',
        title: 'ވޯޓާއި ނިންމުންތައް',
        desc: 'ހުށަހެޅުންތަކާއި ފެންނަ/ނުފެންނަ ވޯޓު ނަމާއެކު ރެކޯޑްކުރެވޭ.',
      },
      {
        icon: 'auto_awesome',
        title: 'ދިވެހިން ލިޔެދޭ އެހީ',
        desc: 'ޕޮއިންޓްތަކުން ރަސްމީ ދިވެހި ބަހުން ބަހުސްގެ ޚުލާސާ AI އިން ލިޔެދޭ.',
      },
      {
        icon: 'file_copy',
        title: 'ނިންމުން / ހާޒިރީ PDF',
        desc: 'ޔައުމިއްޔާ ފާސްވުމުން ނިންމުމާއި ހާޒިރީގެ ސޮއިކުރި ތާނަ PDF ތައްޔާރުކޮށްދޭ.',
      },
      {
        icon: 'speed',
        title: 'ތަންފީޒު ބެލެހެއްޓުން',
        desc: 'ފާސްވި ނިންމުންތައް ތަންފީޒުވަމުންދާ ގޮތް ބަލަހައްޓާ.',
      },
    ],
    icon: 'gavel',
    url: 'https://meetings.wapplify.app',
    mock: 'meet',
  },
  {
    key: 'wapstat',
    name: 'ވެޕްސްޓެޓް',
    category: 'ބޯހިމެނުމާއި ދަފްތަރު',
    tagline: 'ރަށުގެ އާބާދީ ރިކޯޑުކުރުން، ދަފްތަރުތައް ބެލެހެއްޓުން އަދި ސާވޭ ކުރުން',
    blurb:
      'ބޯހިމެނުން ކުރިއަށް ގެންދިއުމާއި، ރައްޔިތުންގެ ސާވޭތައް އަދި ދަފްތަރުތައް ބެލެހެއްޓުން',
    bullets: [
      'ރަށުގެ ރެޖިސްޓްރީ އަދި ދިރިއުޅޭ އާބާދީ އަދާހަމަކޮށް ބެލެހެއްޓުން',
      'އިދާރީގޮތުން ބަލަހައްޓަންޖެހޭ ދަފްތަރުތައް ބެލެހެއްޓުން',
      'ރަށު ލެވެލްގެ ސާވޭތައް ކުރިއަށް ގެންދިއުން',
      'ރަށުގައި ދިރިއުޅޭ ފަރާތްތަކުގެ އެކިއެކި މައުލޫމާތު ނަގައި ބެލެހެއްޓުން',
    ],
    features: [
      {
        icon: 'door_open',
        title: 'ދޮރުން ދޮރަށް ބޯހިމެނުން',
        desc: 'ގެއިން ގެއަށް ގޮސް ގޭގޭގެ މީހުންނާއި މަޢުލޫމާތު ރެކޯޑްކުރެވޭ.',
      },
      {
        icon: 'group',
        title: 'ދިރިހުރި ދަފްތަރު',
        desc: 'މިހާރު ދިރިއުޅޭ ފަރާތްތަކުގެ އަޕް-ޓު-ޑޭޓް ދަފްތަރެއް.',
      },
      {
        icon: 'swap_horiz',
        title: 'ބަދަލުތައް ބެލެހެއްޓުން',
        desc: 'އުފަންވުން، މަރުވުން އަދި ބަދަލުވުން ތާރީޚާއެކު ރެކޯޑްކުރެވޭ.',
      },
      {
        icon: 'storefront',
        title: 'ގޭގޭގެ ވިޔަފާރި',
        desc: 'ރަޖިސްޓަރީކުރި އަދި ގޭގައި ހިންގާ ވިޔަފާރިތައް ބަލަހައްޓާ.',
      },
      {
        icon: 'description',
        title: 'ދަފްތަރު PDF ތާނައިން',
        desc: 'ރަސްމީ ދަފްތަރު ތާނަ PDF ގޮތުގައި ތައްޔާރުކޮށްދޭ.',
      },
      {
        icon: 'table_chart',
        title: 'ސްމާޓް ދަފްތަރު',
        desc: 'އަމިއްލައަށް ހޮވައިގެން ނުވަތަ ފިލްޓަރުން އޮޓަމެޓިކުން ދަފްތަރު ހަދާ، ކޮލަމް ކަސްޓަމައިޒް ކުރެވޭ.',
      },
      {
        icon: 'assignment',
        title: 'ސާވޭ',
        desc: 'ސުވާލުތަކާއެކު ސާވޭ ހަދައި، ޗާޓާއި އެކްސެލް ގޮތުގައި ނަތީޖާ ބަލާ.',
      },
      {
        icon: 'dynamic_form',
        title: 'ފޯމް ބިލްޑަރ',
        desc: 'ރައްޔިތުންނާއި ވިޔަފާރީގެ ފޯމް ފީލްޑްތައް އެޑްމިން އަމިއްލައަށް ބައްޓަންކުރޭ.',
      },
    ],
    icon: 'assignment',
    url: 'https://stats.wapplify.app',
    mock: 'stat',
    inDevelopment: true,
  },
  {
    key: 'wappro',
    name: 'ވެޕްޕްރޯ',
    category: 'ޕްރޮކިއުމަންޓް',
    tagline: 'ކަރުދާހެއް ނެތި ޕްރޮކިއުމަންޓް',
    blurb:
      'ވިޔަފާރިވެރިންގެ އަގުތައް ހޯދައި، ޖެހިޖެހިގެން އަޅާކިޔައި އެވޯޑްކުރޭ — ހުރިހާ ލިޔެކިޔުމެއް ތައްޔާރުކޮށްދެނީ.',
    bullets: [
      'މުވައްޒަފުން ތަކެތީގެ ރިކުއެސްޓް ހުށަހަޅާ',
      'ގޭގައި މަސައްކަތްކުރާ ވިޔަފާރިވެރިން ލޮގިން ނެތި ލިންކުން އަގު ދޭ',
      'އަގުތައް އަޅާކިޔައި އެއް ތަނަކުން އެވޯޑްކުރެވޭ',
      'ކޯޓޭޝަން، އިންވޮއިސް އަދި ޕާޗޭސް-އޯޑަރ PDF',
    ],
    features: [
      {
        icon: 'assignment',
        title: 'ރިކުއެސްޓް (GSR)',
        desc: 'މުވައްޒަފުން ތަކެއްޗާއި ޚިދުމަތުގެ ރިކުއެސްޓް ލައިން-އައިޓަމާއެކު ހަދާ.',
      },
      {
        icon: 'verified',
        title: 'ސޮއި އަދި އެޕްރޫވަލް',
        desc: 'GSR އަދި ޕާޗޭސް ރިކުއެސްޓް ވެޕްސައިން މެދުވެރިކޮށް ސޮއިކޮށް ފާސްކުރެވޭ.',
      },
      {
        icon: 'inventory_2',
        title: 'ޕާޗޭސް ރިކުއެސްޓް',
        desc: 'ފާސްވި ތަކެތީގެ GSR އިން ޕާޗޭސް ރިކުއެސްޓް އޮޓަމެޓިކުން ހަދާ.',
      },
      {
        icon: 'badge',
        title: 'ID ކާޑުން ވިޔަފާރިވެރިން',
        desc: 'އީމެއިލް/އެސްއެމްއެސް ނެތި، ID ކާޑު ނަންބަރުން ވިޔަފާރިވެރިން ރަޖިސްޓަރީކުރެވޭ.',
      },
      {
        icon: 'link',
        title: 'ލޮގިން ނެތި އަގު ދިނުން',
        desc: 'ވިޔަފާރިވެރިން ލޮގިން ނެތި ޕަބްލިކް ލިންކަކުން އަގު ހުށަހަޅާ.',
      },
      {
        icon: 'login',
        title: 'ވިޔަފާރިވެރިންގެ ޕޯޓަލް',
        desc: 'ބޭނުންނަމަ ލޮގިންވެ، ހުޅުވިފައިވާ ރިކުއެސްޓް، އަގުތައް އަދި އެވޯޑް ބަލާ.',
      },
      {
        icon: 'balance',
        title: 'އަގުތައް އަޅާކިޔުން',
        desc: 'ވިޔަފާރިވެރިންގެ އަގުތައް ޖެހިޖެހިގެން އަޅާކިޔައި، އެންމެ ދަށް އަގު ދައްކައިދޭ.',
      },
      {
        icon: 'workspace_premium',
        title: 'އެވޯޑް + PDF',
        desc: 'އެވޯޑްކޮށް، ފައިސާ ދެއްކުން ބަލަހައްޓައި، ކޯޓޭޝަން/އިންވޮއިސް/PO ތާނަ PDF ތައްޔާރުކުރޭ.',
      },
    ],
    icon: 'shopping_cart',
    url: 'https://pro.wapplify.app',
    mock: 'pro',
    inDevelopment: true,
    hidden: true,
  },
  {
    // Registered in data but hidden from the public marketing surface until its
    // Dhivehi marketing copy + a dedicated mock screen exist (mirrors WapPro's
    // current `hidden` treatment). Remove `hidden` to bring it back everywhere.
    key: 'wapdocs',
    name: 'ވެޕްޑޮކްސް',
    category: 'ލިޔެކިޔުން',
    tagline: 'Council documents',
    blurb: 'Council documents',
    bullets: [],
    features: [],
    icon: 'description',
    url: 'https://docs.wapplify.app',
    mock: 'sign',
    hidden: true,
  },
];

// The public site shows only non-hidden products. WapPro is currently hidden;
// remove its `hidden` flag above to bring it back everywhere at once.
export const products: Product[] = allProducts.filter((p) => !p.hidden);
